import { createServerFn } from "@tanstack/solid-start";
import { z } from "zod";
import { withSentrySpan } from "~/lib/sentry-span";
import { authFunctionMiddleware } from "~/server/middleware/auth";

export const GetDashboardPageDataInputSchema = z.object({
  businessId: z.string().trim().min(1),
});

/**
 * Aggregate server function for the dashboard overview page.
 *
 * Fetches all dashboard data (account overview, depreciation overview,
 * recent journal entries, recent documents) in a single HTTP round trip
 * on client-side navigation.
 */
export const getDashboardPageData = createServerFn({ method: "GET" })
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) => GetDashboardPageDataInputSchema.parse(i))
  .handler(async ({ context, data }) => {
    return withSentrySpan("dashboard.pageData", async () => {
      const [
        accountOverviewMod,
        depreciationOverviewMod,
        journalEntryMod,
        documentMod,
      ] = await Promise.all([
        import("../account/account-overview.server"),
        import("../fixed-assets/depreciation-overview.server"),
        import("../journal-entry/journal-entry.server"),
        import("../document/document.server"),
      ]);
      const { getAccountOverviewForUser } = accountOverviewMod;
      const { getDepreciationOverviewForUser } = depreciationOverviewMod;
      const { listJournalEntriesForUser } = journalEntryMod;
      const { listDocumentsForUser } = documentMod;

      const [
        accountOverview,
        depreciationOverview,
        recentJournalEntries,
        recentDocuments,
      ] = await Promise.all([
        getAccountOverviewForUser(context.user.id, data.businessId),
        getDepreciationOverviewForUser(context.user.id, data.businessId),
        listJournalEntriesForUser(context.user.id, data.businessId, {
          limit: 5,
        }),
        listDocumentsForUser(context.user.id, data.businessId, {
          limit: 5,
        }),
      ]);

      return {
        accountOverview,
        depreciationOverview,
        recentJournalEntries,
        recentDocuments,
      } as const;
    });
  });
